import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { Grid, Button } from '@mui/material';
import { Add as AddIcon, Download as DownloadIcon } from '@mui/icons-material';
import { toast } from '../../components/toast/modernToast';
import { useLoadingState } from '../../hooks/useLoadingState';
import DeleteConfirmationDialog from '../../components/common/DeleteConfirmationDialog';
import {
  PayrollStatsCards,
  PayrollFilters,
  PayrollTable,
  PayrollForm,
  BatchPayrollDialog,
  BatchEditPayrollDialog,
  PayrollPDFDialog,
} from '../../components/payroll';
import { payrollValidationSchema } from '../../utils/payrollValidation';
import {
  getInitialPayrollFormData,
  getPayrollFormDataFromRecord,
  formatPayrollDataForSubmission,
  calculateGrossPayable,
  calculateNetPayable,
} from '../../utils/payrollUtils';
import { HRPageHeader } from '../../components/hr';
import { LightTooltip } from '../../components/custom-components/custom-tooltip';

import { getUserGroups } from '../../utils';

import {
  getEmployeeDetails,
  getPayrolls,
  createPayroll,
  updatePayroll,
  deletePayroll,
  batchCreatePayroll,
  getPayrollFile,
} from '../../services/api';

const allowedGroups = ['admin', 'hr'];

function PayrollProcessingPage() {
  const getPreviousPayrollPeriod = () => {
    const now = new Date();
    const previousMonthDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    return {
      month: previousMonthDate.toLocaleString('default', { month: 'long' }),
      year: now.getFullYear(),
    };
  };

  const initialPayrollPeriod = getPreviousPayrollPeriod();

  const navigate = useNavigate();
  const [payrolls, setPayrolls] = useState([]);
  const [filterOptions, setFilterOptions] = useState({});
  const [stats, setStats] = useState({
    total_payrolls: 0,
    total_gross_pay: 0,
    total_net_pay: 0,
    current_month_payrolls: 0,
  });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    employee: '',
    month: initialPayrollPeriod.month,
    year: initialPayrollPeriod.year,
    payment_status: '',
  });

  const { isLoading, withLoading } = useLoadingState({ minLoadingTime: 800 });

  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openBatchDialog, setOpenBatchDialog] = useState(false);
  const [openBatchEditDialog, setOpenBatchEditDialog] = useState(false);
  const [openPDFDialog, setOpenPDFDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState('add');
  const [selectedPayroll, setSelectedPayroll] = useState(null);

  const [formData, setFormData] = useState(getInitialPayrollFormData());
  const [batchFormData, setBatchFormData] = useState({
    month: initialPayrollPeriod.month,
    year: initialPayrollPeriod.year,
  });
  const [pdfFormData, setPdfFormData] = useState({
    month: initialPayrollPeriod.month,
    year: initialPayrollPeriod.year,
    file_type: 'PDF',
    payment_date: new Date(),
    update_payment_date: false,
    create_expense_entry: true,
  });

  const [formErrors, setFormErrors] = useState({});
  const [batchFormErrors, setBatchFormErrors] = useState({});
  const [pdfFormErrors, setPdfFormErrors] = useState({});
  const [isBatchSubmitting, setIsBatchSubmitting] = useState(false);
  const [isPdfSubmitting, setIsPdfSubmitting] = useState(false);

  const initializeFormData = () => {
    setFormData(getInitialPayrollFormData());
    setFormErrors({});
  };

  const loadPayrolls = async () => {
    await withLoading(async () => {
      try {
        const response = await getPayrolls(
          page,
          10,
          searchTerm,
          filters.employee,
          filters.month,
          filters.year,
          filters.payment_status,
          ''
        );

        const { data } = response;
        setPayrolls(data.results || []);
        setStats(data.stats || {});

        // Update filter options if included in response
        if (data.filter_options) {
          setFilterOptions(data.filter_options);
        }

        if (data.pagination_info) {
          setTotalPages(data.pagination_info.total_pages);
        } else {
          setTotalPages(1);
        }
      } catch (error) {
        console.error('Error loading payrolls:', error);
        setPayrolls([]);
        setStats({});
      }
    });
  };

  useEffect(() => {
    const userGroups = getUserGroups();
    if (userGroups === null || !allowedGroups.some((group) => userGroups.includes(group))) {
      navigate('/dashboard', { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    loadPayrolls();
  }, [page, filters, searchTerm]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleInputChange = async (field, value) => {
    const newData = { ...formData, [field]: value };

    if (field === 'employee' && value) {
      try {
        const response = await getEmployeeDetails(value);
        const emp = response.data.employee;
        newData.basic_salary = emp.basic_salary || 0;
        newData.transport_allowance = emp.transport_allowance || 0;
        newData.payment_currency = emp.salary_currency || 'PKR';
        newData.bonus_dues = emp.bonus_dues || 0;
        newData.weekends_pay = emp.weekends_pay || 0;
        newData.tax_deductions = emp.tax_deductions || 0;
      } catch (error) {
        console.error('Error fetching employee details:', error);
      }
    }

    if (
      [
        'employee',
        'basic_salary',
        'transport_allowance',
        'bonus_dues',
        'weekends_pay',
        'absent_deductions',
        'loan_deductions',
        'tax_deductions',
        'other_deductions',
        'late_deductions',
      ].includes(field)
    ) {
      newData.gross_payable = calculateGrossPayable(newData);
      newData.net_payable = calculateNetPayable(newData);
    }

    setFormData(newData);

    if (formErrors[field]) {
      setFormErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setPage(1);
  };

  const handleClearFilters = () => {
    const previousPayrollPeriod = getPreviousPayrollPeriod();
    setFilters({
      employee: '',
      month: previousPayrollPeriod.month,
      year: previousPayrollPeriod.year,
      payment_status: '',
    });
    setSearchTerm('');
    setPage(1);
  };

  const handleOpenDialog = (mode, payroll = null) => {
    setDialogMode(mode);
    setSelectedPayroll(payroll);

    if (mode === 'add') {
      initializeFormData();
    } else if (payroll) {
      setFormData(getPayrollFormDataFromRecord(payroll));
    }

    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedPayroll(null);
    setFormErrors({});
  };

  const handleSubmit = async () => {
    try {
      await payrollValidationSchema.validate(formData, { abortEarly: false });
      setFormErrors({});

      const submitData = formatPayrollDataForSubmission(formData);

      if (dialogMode === 'add') {
        await createPayroll(submitData);
        toast.success('Payroll created successfully');
      } else if (dialogMode === 'edit') {
        await updatePayroll(selectedPayroll.id, submitData);
        toast.success('Payroll updated successfully');
      }

      handleCloseDialog();
      loadPayrolls();
    } catch (error) {
      if (error.name === 'ValidationError') {
        const errors = {};
        error.inner.forEach((err) => {
          errors[err.path] = err.message;
        });
        setFormErrors(errors);
      } else {
        console.error('Error submitting form:', error);
        toast.error(error?.response?.data?.message || 'Failed to save payroll');
      }
    }
  };

  const handleDeleteClick = (payroll) => {
    setSelectedPayroll(payroll);
    setOpenDeleteDialog(true);
  };

  const handleCancelDelete = () => {
    setOpenDeleteDialog(false);
    setSelectedPayroll(null);
  };

  const handleConfirmDelete = async () => {
    try {
      await deletePayroll(selectedPayroll.id);
      setOpenDeleteDialog(false);
      setSelectedPayroll(null);
      loadPayrolls();
      toast.success('Payroll deleted successfully');
    } catch (error) {
      console.error('Error deleting payroll:', error);
    }
  };

  const handleBatchInputChange = (field, value) => {
    setBatchFormData((prev) => ({ ...prev, [field]: value }));
    if (batchFormErrors[field]) {
      setBatchFormErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const handleOpenBatchDialog = () => {
    const previousPayrollPeriod = getPreviousPayrollPeriod();
    setBatchFormData({
      month: previousPayrollPeriod.month,
      year: previousPayrollPeriod.year,
    });
    setBatchFormErrors({});
    setOpenBatchDialog(true);
  };

  const handlePDFInputChange = (field, value) => {
    setPdfFormData((prev) => {
      const next = { ...prev, [field]: value };

      if (field === 'file_type' && value === 'SalarySlip') {
        next.update_payment_date = false;
        next.create_expense_entry = false;
      }

      return next;
    });
    if (pdfFormErrors[field]) {
      setPdfFormErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const handleOpenPDFDialog = () => {
    const previousPayrollPeriod = getPreviousPayrollPeriod();
    setPdfFormData({
      month: previousPayrollPeriod.month,
      year: previousPayrollPeriod.year,
      file_type: 'PDF',
      payment_date: new Date(),
      update_payment_date: false,
      create_expense_entry: true,
    });
    setPdfFormErrors({});
    setOpenPDFDialog(true);
  };

  const handleClosePDFDialog = () => {
    setOpenPDFDialog(false);
    setPdfFormErrors({});
  };

  const handleSinglePayrollSlipDownload = async (payroll) => {
    try {
      toast.info('Generating single salary slip PDF...');

      const response = await getPayrollFile({
        file_type: 'SalarySlip',
        payroll_id: payroll.id,
        update_payment_date: false,
        create_expense_entry: false,
      });

      const contentType = response.headers['content-type'] || 'application/pdf';
      const blob = new Blob([response.data], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const disposition = response.headers['content-disposition'] || '';
      const nameMatch = disposition.match(/filename="?([^";]+)"?/i);
      link.download = nameMatch?.[1] || `salary_slip_${payroll.employee?.employee_id || payroll.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Salary slip PDF downloaded successfully');
    } catch (error) {
      console.error('Error downloading salary slip PDF:', error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Failed to download salary slip PDF');
      }
    }
  };

  const handleCloseBatchDialog = () => {
    setOpenBatchDialog(false);
    setBatchFormErrors({});
  };

  const handleBatchSubmit = async () => {
    try {
      setIsBatchSubmitting(true);
      setBatchFormErrors({});

      // Basic validation
      const errors = {};
      if (!batchFormData.month) errors.month = 'Month is required';
      if (!batchFormData.year) errors.year = 'Year is required';

      if (Object.keys(errors).length > 0) {
        setBatchFormErrors(errors);
        return;
      }

      const submitData = {
        month: batchFormData.month,
        year: batchFormData.year,
      };

      const response = await batchCreatePayroll(submitData);

      if (response.data.status === 'success') {
        const { summary, skipped_employees: skippedEmployees } = response.data;

        let message = `Successfully created ${summary.payrolls_created} payroll records`;
        if (summary.employees_skipped > 0) {
          message += ` (${summary.employees_skipped} employees skipped)`;
        }

        toast.success(message);

        // Show detailed information if there were skipped employees
        if (skippedEmployees.length > 0) {
          console.log('Skipped employees:', skippedEmployees);
        }

        handleCloseBatchDialog();
        loadPayrolls();
      } else {
        toast.error(response.data.message || 'Failed to create batch payroll');
      }
    } catch (error) {
      console.error('Error creating batch payroll:', error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Failed to create batch payroll');
      }
    } finally {
      setIsBatchSubmitting(false);
    }
  };

  const handleDownloadPayrollExport = async () => {
    try {
      setIsPdfSubmitting(true);
      setPdfFormErrors({});

      const errors = {};
      if (!pdfFormData.month) errors.month = 'Month is required';
      if (!pdfFormData.year) errors.year = 'Year is required';
      if (!pdfFormData.file_type) errors.file_type = 'File type is required';
      if (pdfFormData.update_payment_date && !pdfFormData.payment_date) {
        errors.payment_date = 'Payment date is required when update payment date is enabled';
      }

      if (Object.keys(errors).length > 0) {
        setPdfFormErrors(errors);
        return;
      }

      toast.info(
        `Generating ${
          pdfFormData.file_type === 'SalarySlip' ? 'salary slip file' : `payroll ${pdfFormData.file_type}`
        }...`
      );

      const response = await getPayrollFile({
        month: pdfFormData.month,
        year: pdfFormData.year,
        file_type: pdfFormData.file_type,
        update_payment_date: pdfFormData.update_payment_date,
        ...(pdfFormData.update_payment_date && pdfFormData.payment_date
          ? { payment_date: pdfFormData.payment_date.toISOString().split('T')[0] }
          : {}),
        create_expense_entry: pdfFormData.create_expense_entry,
      });

      const contentType = response.headers['content-type'] || 'application/octet-stream';
      const blob = new Blob([response.data], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const disposition = response.headers['content-disposition'] || '';
      const nameMatch = disposition.match(/filename="?([^";]+)"?/i);
      const fallbackName =
        pdfFormData.file_type === 'SalarySlip'
          ? `Salary_Slips_${pdfFormData.month}_${pdfFormData.year}.zip`
          : `Payroll_${pdfFormData.month}_${pdfFormData.year}.${pdfFormData.file_type === 'PDF' ? 'pdf' : 'xlsx'}`;
      link.download = nameMatch?.[1] || fallbackName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Download completed successfully');
      handleClosePDFDialog();
    } catch (error) {
      console.error('Error downloading payroll export:', error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else if (error.response?.status === 404) {
        toast.error('No matching payroll records found');
      } else {
        toast.error(`Failed to download ${pdfFormData.file_type || 'file'}`);
      }
    } finally {
      setIsPdfSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>BillerBay Transmit | Payroll Processing</title>
      </Helmet>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <HRPageHeader
            title="Payroll processing"
            description="Run payroll for pay periods, adjust earnings and deductions, and download payslips or batch summaries for your team."
            actions={
              <>
                <LightTooltip title="Download payroll documents or exports for the current filters.">
                  <Button variant="outlined" onClick={handleOpenPDFDialog} startIcon={<DownloadIcon />} color="primary">
                    Download
                  </Button>
                </LightTooltip>
                <LightTooltip title="Edit multiple payroll records in one session.">
                  <Button variant="outlined" onClick={() => setOpenBatchEditDialog(true)} color="secondary">
                    Batch edit
                  </Button>
                </LightTooltip>
                <LightTooltip title="Generate payroll for many employees at once.">
                  <Button variant="contained" onClick={handleOpenBatchDialog}>
                    Batch payroll
                  </Button>
                </LightTooltip>
                <LightTooltip title="Create a payroll entry for a single employee.">
                  <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog('add')}>
                    Add payroll
                  </Button>
                </LightTooltip>
              </>
            }
          />
        </Grid>

        <PayrollStatsCards stats={stats} isLoading={isLoading} />

        <PayrollFilters
          searchTerm={searchTerm}
          filters={filters}
          filterOptions={filterOptions}
          onSearchChange={setSearchTerm}
          onFilterChange={handleFilterChange}
          onClearFilters={handleClearFilters}
        />
        <PayrollTable
          payrolls={payrolls}
          isLoading={isLoading}
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
          onView={(payroll) => handleOpenDialog('view', payroll)}
          onEdit={(payroll) => handleOpenDialog('edit', payroll)}
          onDelete={handleDeleteClick}
          onDownloadSlip={handleSinglePayrollSlipDownload}
        />
      </Grid>

      <PayrollForm
        open={openDialog}
        mode={dialogMode}
        formData={formData}
        formErrors={formErrors}
        initialEmployee={selectedPayroll?.employee}
        onClose={handleCloseDialog}
        onSubmit={handleSubmit}
        onInputChange={handleInputChange}
      />

      <DeleteConfirmationDialog
        open={openDeleteDialog}
        title="Confirm Delete"
        message="Are you sure you want to delete the payroll record for"
        itemName={
          selectedPayroll
            ? `${selectedPayroll.employee?.full_name} - ${selectedPayroll.month} ${selectedPayroll.year}`
            : ''
        }
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        confirmButtonText="Delete Payroll"
      />

      <BatchPayrollDialog
        open={openBatchDialog}
        onClose={handleCloseBatchDialog}
        onSubmit={handleBatchSubmit}
        formData={batchFormData}
        onInputChange={handleBatchInputChange}
        formErrors={batchFormErrors}
        isSubmitting={isBatchSubmitting}
      />

      <PayrollPDFDialog
        open={openPDFDialog}
        onClose={handleClosePDFDialog}
        onSubmit={handleDownloadPayrollExport}
        formData={pdfFormData}
        onInputChange={handlePDFInputChange}
        formErrors={pdfFormErrors}
        isSubmitting={isPdfSubmitting}
      />

      <BatchEditPayrollDialog
        open={openBatchEditDialog}
        onClose={() => setOpenBatchEditDialog(false)}
        onPayrollUpdated={loadPayrolls}
      />
    </>
  );
}

export default PayrollProcessingPage;
